import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { commandApi } from '../../../services/birdbrain-api-client'
import { serverApiFetch } from '../../../services/server-api-client'
import { errorMessage } from '@/lib/utils'

// Types for API keys
export interface ApiKey {
  id: string
  name: string
  scopes: string[]
  createdByUserId: string
  createdByName?: string
  createdAt: string
  expiresAt?: string | null
  lastUsedAt?: string | null
  isActive: boolean
}

export interface AvailableScope {
  scope: string
  label: string
  description: string
}

// API response types
interface ApiKeysListResponse {
  keys: ApiKey[]
  total: number
  timestamp: string
}

interface AvailableScopesResponse {
  scopes: AvailableScope[]
}

interface CreateApiKeyRequest {
  name: string
  scopes: string[]
  expiresAt?: string
}

interface CreateApiKeyResponse {
  success: boolean
  key: ApiKey
  plainKey: string
  message: string
}

interface ApiKeyResponse {
  success: boolean
  message?: string
  key?: ApiKey
}

// Slice state
interface ApiKeysState {
  keys: ApiKey[]
  availableScopes: AvailableScope[]
  loading: boolean
  scopesLoading: boolean
  creating: boolean
  revoking: boolean
  deleting: boolean
  error: string | null
  // For showing the created key (only shown once)
  createdKey: {
    name: string
    plainKey: string
    scopes: string[]
  } | null
}

const initialState: ApiKeysState = {
  keys: [],
  availableScopes: [],
  loading: false,
  scopesLoading: false,
  creating: false,
  revoking: false,
  deleting: false,
  error: null,
  createdKey: null,
}

// Async thunks using serverApiFetch (same pattern as users.slice.ts)
export const fetchApiKeys = createAsyncThunk(
  'apiKeys/fetchApiKeys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await serverApiFetch<ApiKeysListResponse>('/v1/admin/api-keys')
      return response.keys
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'Failed to fetch API keys' })
    }
  }
)

export const fetchAvailableScopes = createAsyncThunk(
  'apiKeys/fetchAvailableScopes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await serverApiFetch<AvailableScopesResponse>('/v1/admin/api-keys/scopes')
      return response.scopes
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'Failed to fetch available scopes' })
    }
  }
)

// Mutations flow through the birdbrain-api command proxy so every key
// create/revoke/delete lands on the admin_actions audit trail (Command Log).

export const createApiKey = createAsyncThunk(
  'apiKeys/createApiKey',
  async (data: CreateApiKeyRequest, { rejectWithValue }) => {
    try {
      const result = await commandApi.createApiKey(data)
      const response = result.response as CreateApiKeyResponse

      if (!response?.success) {
        return rejectWithValue({ message: response?.message || 'Failed to create API key' })
      }

      return {
        key: response.key,
        plainKey: response.plainKey,
      }
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'Failed to create API key' })
    }
  }
)

export const revokeApiKey = createAsyncThunk(
  'apiKeys/revokeApiKey',
  async ({ keyId, name }: { keyId: string; name?: string }, { rejectWithValue }) => {
    try {
      const result = await commandApi.revokeApiKey(keyId, { targetLabel: name })
      const response = result.response as ApiKeyResponse

      if (!response?.success) {
        return rejectWithValue({ message: response?.message || 'Failed to revoke API key' })
      }

      return keyId
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'Failed to revoke API key' })
    }
  }
)

export const deleteApiKey = createAsyncThunk(
  'apiKeys/deleteApiKey',
  async ({ keyId, name }: { keyId: string; name?: string }, { rejectWithValue }) => {
    try {
      const result = await commandApi.deleteApiKey(keyId, { targetLabel: name })
      const response = result.response as ApiKeyResponse

      if (!response?.success) {
        return rejectWithValue({ message: response?.message || 'Failed to delete API key' })
      }

      return keyId
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'Failed to delete API key' })
    }
  }
)

// Slice
const apiKeysSlice = createSlice({
  name: 'apiKeys',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearCreatedKey: state => {
      state.createdKey = null
    },
  },
  extraReducers: builder => {
    // Fetch API keys
    builder
      .addCase(fetchApiKeys.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchApiKeys.fulfilled, (state, action) => {
        state.loading = false
        state.keys = action.payload
      })
      .addCase(fetchApiKeys.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch API keys'
      })

    // Fetch available scopes
    builder
      .addCase(fetchAvailableScopes.pending, state => {
        state.scopesLoading = true
      })
      .addCase(fetchAvailableScopes.fulfilled, (state, action) => {
        state.scopesLoading = false
        state.availableScopes = action.payload
      })
      .addCase(fetchAvailableScopes.rejected, (state, action) => {
        state.scopesLoading = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch available scopes'
      })

    // Create API key
    builder
      .addCase(createApiKey.pending, state => {
        state.creating = true
        state.error = null
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.creating = false
        state.keys.unshift(action.payload.key)
        state.createdKey = {
          name: action.payload.key.name,
          plainKey: action.payload.plainKey,
          scopes: action.payload.key.scopes,
        }
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.creating = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to create API key'
      })

    // Revoke API key
    builder
      .addCase(revokeApiKey.pending, state => {
        state.revoking = true
        state.error = null
      })
      .addCase(revokeApiKey.fulfilled, (state, action) => {
        state.revoking = false
        const keyIndex = state.keys.findIndex(k => k.id === action.payload)
        if (keyIndex !== -1) {
          state.keys[keyIndex].isActive = false
        }
      })
      .addCase(revokeApiKey.rejected, (state, action) => {
        state.revoking = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to revoke API key'
      })

    // Delete API key
    builder
      .addCase(deleteApiKey.pending, state => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteApiKey.fulfilled, (state, action) => {
        state.deleting = false
        state.keys = state.keys.filter(k => k.id !== action.payload)
      })
      .addCase(deleteApiKey.rejected, (state, action) => {
        state.deleting = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to delete API key'
      })
  },
})

export const { clearError, clearCreatedKey } = apiKeysSlice.actions
export const apiKeysReducer = apiKeysSlice.reducer

// Base selector
const selectApiKeysState = (state: { apiKeys: ApiKeysState }) => state.apiKeys

// Memoized selectors
export const selectApiKeys = createSelector([selectApiKeysState], apiKeysState => apiKeysState.keys)

export const selectAvailableScopes = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.availableScopes
)

export const selectApiKeysLoading = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.loading
)

export const selectScopesLoading = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.scopesLoading
)

export const selectApiKeysCreating = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.creating
)

export const selectApiKeysRevoking = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.revoking
)

export const selectApiKeysDeleting = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.deleting
)

export const selectApiKeysError = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.error
)

export const selectCreatedKey = createSelector(
  [selectApiKeysState],
  apiKeysState => apiKeysState.createdKey
)
